import { Component, OnInit, Input, Self, Optional, ViewChild, Output, EventEmitter, ElementRef, SimpleChanges, OnChanges } from '@angular/core';
import { NgControl, ControlValueAccessor } from '@angular/forms';
import * as uuid from 'uuid';

@Component({
    selector: 'app-form-input-v2',
    templateUrl: './input-v2.component.html',
    styleUrls: ['./input-v2.component.scss'],
})
export class FormInputV2Component implements OnInit, ControlValueAccessor, OnChanges {
    public onChange: (value: any) => void = () => {
        /* This will be replaced by registerOnChange */
    };

    public onTouched: () => void = () => {
        /* This will be replaced by registerOnTouched */
    };

    public id: string;

    public _value: any = '';

    @Input() disabled: boolean;

    @Input() label: string;

    /** Optional helper text rendered as a subtitle directly under the label. */
    @Input() subtitle: string;

    @Input() placeholder?: string;

    @Input() popoverTitle: string;

    @Input() popoverBody: string;

    @Input() hint: string;

    @Input() type: 'text' | 'email' | 'password' | 'phone' | 'number' = 'text';

    @Input() validation = true;

    @Input() messages = false;

    @Input() charLimit: number;

    @Input() invalidPass: boolean;

    @Input() invalidText: string;

    @Output('change') changeOutput = new EventEmitter<any>();

    @ViewChild('input', { static: false }) input: ElementRef;

    get currentCharactersAmount(): number {
        return this._value?.length ?? 0;
    }

    get hasMaxLengthError(): boolean {
        if (!this.ngControl) return false;
        return this.ngControl.errors?.maxlength !== undefined;
    }

    get placeholderText(): string {
        return this.placeholder ?? this.label ?? '';
    }

    constructor(@Self() @Optional() private ngControl: NgControl) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnInit(): void {
        this.id = uuid.v4();

        if (this.ngControl && this.ngControl.value !== null && this.ngControl.value !== undefined) {
            this._value = this.ngControl.value;
        }
    }

    ngOnChanges(_changes: SimpleChanges): void {
        if (this.ngControl && this.ngControl.value !== this._value) {
            this._value = this.ngControl.value;
            this.updateInputElementValue();
        }
    }

    private updateInputElementValue(): void {
        setTimeout(() => {
            const inputElement = document.getElementById(this.id) as HTMLInputElement;
            if (inputElement) {
                if (inputElement.value !== this._value) {
                    inputElement.value = this._value || '';

                    inputElement.dispatchEvent(new Event('input', { bubbles: true }));
                    inputElement.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        }, 0);
    }

    public forceValueUpdate(value: any): void {
        this._value = value;
        this.updateInputElementValue();
        if (this.onChange) {
            this.onChange(value);
        }
        if (this.onTouched) {
            this.onTouched();
        }
        this.changeOutput.emit(value);
    }

    public directSetValue(value: any): void {
        this._value = value;

        setTimeout(() => {
            if (this.input?.nativeElement) {
                const inputElement = this.input.nativeElement as HTMLInputElement;
                inputElement.value = value || '';

                inputElement.dispatchEvent(new Event('input', { bubbles: true }));
                inputElement.dispatchEvent(new Event('change', { bubbles: true }));
                inputElement.dispatchEvent(new Event('blur', { bubbles: true }));
            } else {
                const inputElement = document.getElementById(this.id) as HTMLInputElement;
                if (inputElement) {
                    inputElement.value = value || '';

                    inputElement.dispatchEvent(new Event('input', { bubbles: true }));
                    inputElement.dispatchEvent(new Event('change', { bubbles: true }));
                    inputElement.dispatchEvent(new Event('blur', { bubbles: true }));
                }
            }

            if (this.onChange) {
                this.onChange(value);
                this.onTouched();
            }

            this.changeOutput.emit(value);
        });
    }

    get invalid(): boolean {
        if (this.ngControl) {
            return this.ngControl.invalid;
        }
        return false;
    }

    get valid(): boolean {
        if (this.ngControl) {
            return this.ngControl.valid;
        }
        return false;
    }

    get pristine(): boolean {
        if (this.ngControl) {
            return this.ngControl.pristine;
        }
        return false;
    }

    get dirty(): boolean {
        if (this.ngControl) {
            return this.ngControl.dirty;
        }
        return false;
    }

    get touched(): boolean {
        if (this.ngControl) {
            return this.ngControl.touched;
        }
        return false;
    }

    get value(): any {
        return this._value;
    }

    set value(v) {
        this._value = v;
        if (this.onChange) {
            this.changeOutput.emit(v);
            this.onChange(v);
            this.onTouched();
        }
    }

    valueChange(v): void {
        this.value = v;
    }

    onKeyDown(event: KeyboardEvent): void {
        if (this.type === 'number' && (event.key === '-' || event.key === 'Subtract')) {
            event.preventDefault();
        }
    }

    onInput(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        let value = inputElement.value;
        if (this.type === 'number' && value !== '' && Number(value) < 0) {
            value = '0';
            inputElement.value = value;
        }
        this.valueChange(value);
    }

    writeValue(value: any): void {
        if (this._value !== value) {
            this._value = value;
            this.updateInputElementValue();
        }
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
}
